import {
  Order,
  Product,
  ProductComponent,
} from "../../types";
import Decimal from "decimal.js";
import { isCarcaseItem } from "../order_item/isCarcaseItem";
import { getM2Value } from "../order_item/getM2Value";
import { getDiscountPrice } from "../shared/getDiscountPrice";
import { getMarginPrice } from "../shared/getMarginPrice";

/**
 * Hardcoded board component ID
 * TODO: Make this configurable
 */
const BOARD_COMPONENT_ID = "457ae92d-6172-43a4-817a-5119f9a96a53";

export interface BoardUsageResult {
  totalM2: number;
  totalCost: number;
  totalSalesPrice: string;
}

/**
 * Calculate total board material usage (m2) and cost for an order.
 *
 * For standard products: board m2 comes from the board component's qty in the product's components array
 * For soldByM2 products: board m2 is derived from the item's dimensions (h x w) using getM2Value
 *
 * @param order The order to calculate board usage for
 * @param products Array of all products (to look up product details)
 * @param productComponents Array of all product components (to get board price)
 * @returns BoardUsageResult with totalM2 and totalCost
 */
export function getBoardUsage(
  order: Order,
  products: Product[],
  productComponents: ProductComponent[]
): BoardUsageResult {
  // Find the board component to get its price
  const boardComponent = productComponents.find(
    (pc) => pc.id === BOARD_COMPONENT_ID
  );
  const boardPrice = boardComponent?.price ?? 0;
  const boardDiscount = boardComponent?.discount ?? 0;
  const boardMargin = boardComponent?.margin ?? 0;

  let totalM2 = new Decimal(0);
  let totalSalesPrice = new Decimal(0);

  // Process only carcase items
  for (const item of order.items) {
    if (!isCarcaseItem(item)) continue;

    // Find the product for this item
    const product = products.find((p) => p.id === item.productId);
    if (!product) continue;

    let itemM2: number;
    if (product.soldByM2) {
      // For soldByM2 products, calculate m2 from item dimensions
      itemM2 = getM2Value(item);
    } else {
      // For standard products, get board qty from product components
      const productBoardComponent = product.components.find(
        (c) => c.id === BOARD_COMPONENT_ID
      );
      if (!productBoardComponent) continue;
      itemM2 = productBoardComponent.qty;
    }

    const itemBoardCost = new Decimal(itemM2)
      .mul(item.quantity)
      .mul(boardPrice);
    const itemSalePrice = getDiscountPrice(
      itemBoardCost.toNumber(),
      boardDiscount
    );
    const marginPrice = getMarginPrice(itemSalePrice, boardMargin);
    const itemOverallSalesPrice = getDiscountPrice(marginPrice, item.discount);

    // Add quantity-adjusted sales price and m2 to the running totals
    totalSalesPrice = totalSalesPrice.add(new Decimal(itemOverallSalesPrice));
    totalM2 = totalM2.add(new Decimal(itemM2).mul(item.quantity));
  }

  const totalCost = totalM2.mul(boardPrice).toNumber();

  return {
    totalM2: totalM2.toNumber(),
    totalCost,
    totalSalesPrice: totalSalesPrice.toFixed(2),
  };
}
